import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import type { Components } from "react-markdown";
import type { WebsiteIssue } from "../../content/issues";

const STATUS_COLORS: Record<WebsiteIssue["status"], string> = {
  draft: "bg-warm-500/20 text-warm-400 border-warm-500/30",
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  done: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  archived: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

const PRIORITY_COLORS: Record<WebsiteIssue["priority"], string> = {
  high: "bg-rose-500/20 text-rose-400 border-rose-500/30",
  medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  low: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

const STATUS_ORDER: Record<WebsiteIssue["status"], number> = {
  active: 0,
  draft: 1,
  done: 2,
  archived: 3,
};

const PRIORITY_ORDER: Record<WebsiteIssue["priority"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const markdownComponents: Components = {
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const language = match ? match[1] : "";

    return match ? (
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        PreTag="div"
        className="rounded-lg !text-xs"
        customStyle={{ fontSize: "0.75rem", lineHeight: "1.25rem" }}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  },
};

function stripH1(body: string): string {
  const lines = body.split("\n");
  if (lines.length > 0 && lines[0].startsWith("# ")) {
    return lines.slice(1).join("\n").trimStart();
  }
  return body;
}

interface IssuesTableProps {
  issues: WebsiteIssue[];
}

export function IssuesTable({ issues }: IssuesTableProps) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  function timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return t('common.timeAgo.justNow');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t('common.timeAgo.minutesAgo', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('common.timeAgo.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 30) return t('common.timeAgo.daysAgo', { count: days });
    const months = Math.floor(days / 30);
    if (months < 12) return t('common.timeAgo.monthsAgo', { count: months });
    const years = Math.floor(months / 12);
    return t('common.timeAgo.yearsAgo', { count: years });
  }

  function formatFolder(folder: string): string {
    if (!folder) return t('common.folderGeneral');
    return folder.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const toggleExpand = useCallback(
    (id: string) => {
      const isCollapsing = expandedId === id;
      const row = rowRefs.current.get(id);
      const topBefore = row ? row.getBoundingClientRect().top : 0;

      setExpandedId(isCollapsing ? null : id);

      if (row) {
        // Keep the row pinned to its viewport position during expansion
        const pinRow = () => {
          const topAfter = row.getBoundingClientRect().top;
          const drift = topAfter - topBefore;
          if (Math.abs(drift) > 1) {
            window.scrollBy(0, drift);
          }
        };

        // Run for the duration of the animation to counteract layout shifts
        const start = performance.now();
        const animate = () => {
          pinRow();
          if (performance.now() - start < 200) {
            requestAnimationFrame(animate);
          }
        };
        requestAnimationFrame(animate);
      }
    },
    [expandedId]
  );

  const sorted = useMemo(() => {
    return [...issues].sort((a, b) => {
      const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      if (statusDiff !== 0) return statusDiff;

      const priorityDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      if (priorityDiff !== 0) return priorityDiff;

      return a.title.localeCompare(b.title);
    });
  }, [issues]);

  return (
    <div className="bg-dark-card border border-red-800/30 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-warm-500 uppercase tracking-wider border-b border-red-800/30">
            <th className="px-4 py-3 text-left font-medium">{t('common.table.title')}</th>
            <th className="px-4 py-3 text-left font-medium">{t('common.table.folder')}</th>
            <th className="px-4 py-3 text-left font-medium">{t('common.table.status')}</th>
            <th className="px-4 py-3 text-left font-medium">{t('common.table.priority')}</th>
            <th className="px-4 py-3 text-left font-medium">{t('common.table.created')}</th>
            <th className="px-4 py-3 text-left font-medium">{t('common.table.updated')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((issue) => {
            const isExpanded = expandedId === issue.id;
            return (
              <Fragment key={issue.id}>
                <tr
                  ref={(el) => {
                    if (el) rowRefs.current.set(issue.id, el);
                  }}
                  className="border-b border-dark-border/50 hover:bg-dark-hover/50 transition-colors cursor-pointer scroll-mt-20"
                  onClick={() => toggleExpand(issue.id)}
                >
                  <td className="px-4 py-3 text-warm-100">{issue.title}</td>
                  <td className="px-4 py-3 text-warm-400">{formatFolder(issue.folder)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[issue.status]}`}
                    >
                      {issue.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${PRIORITY_COLORS[issue.priority]}`}
                    >
                      {issue.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-warm-400">{timeAgo(issue.createdAt)}</td>
                  <td className="px-4 py-3 text-warm-400">{timeAgo(issue.updatedAt)}</td>
                </tr>
                <tr>
                  <td colSpan={6} className="p-0 border-none">
                    <div
                      className="grid transition-[grid-template-rows] duration-150 ease-in-out"
                      style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
                    >
                      <div className="overflow-hidden">
                        <div className="px-6 py-4 bg-dark-bg/50">
                          <div className="prose prose-invert max-w-none text-sm text-warm-200 [&_pre]:overflow-x-auto">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {stripH1(issue.body)}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
